import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import Layout from '../../../../components/Layout';
import { apiFetch } from '../../../../utils/api';
import { useI18n } from '../../../../hooks/useI18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PlayIcon, ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Input } from "@/components/ui/input";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';


interface MetricDefinition {
  id: string;
  metric_code: string;
  display_name: string;
  description?: string;
  category?: string;
  source_table?: string;
  metric_type: string;
  value_type: string;
  supported_dimensions?: string[];
}

interface MetricDimension {
  id: string;
  dimension_code: string;
  display_name: string;
  dimension_type: string;
}

interface MetricResult {
  dimensions: Record<string, any>;
  value: number;
  formatted_value: string;
}

interface ExecutionResponse {
  metric_name: string;
  metric_code: string;
  results: MetricResult[];
  execution_time_ms: number;
  total_rows: number;
  debug_sql?: string;
  debug_params?: Record<string, any>;
}

export default function MetricExecutePage() {
  const navigate = useNavigate();
  const params = useParams({ from: '/metrics/execute/$id' });
  const { t } = useI18n();

  const { id } = params;

  const [metric, setMetric] = useState<MetricDefinition | null>(null);
  const [dimensions, setDimensions] = useState<MetricDimension[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [timeRangeType, setTimeRangeType] = useState<string>('last_12_months');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [limit, setLimit] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');

  const [results, setResults] = useState<ExecutionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSQL, setShowSQL] = useState<boolean>(false);

  useEffect(() => {
    fetchMetric();
    fetchDimensions();
  }, [id]);

  const fetchMetric = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/metrics/definitions/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch metric');
      }

      const data = await response.json();
      setMetric(data);

      // Don't auto-select dimensions - let user choose
      setSelectedDimensions([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchDimensions = async () => {
    try {
      const response = await apiFetch('/api/metrics/dimensions');

      if (!response.ok) {
        throw new Error('Failed to fetch dimensions');
      }

      const data = await response.json();
      setDimensions(data);
    } catch (err) {
      console.error('Failed to fetch dimensions:', err);
    }
  };

  const executeMetric = async () => {
    if (!metric) return;

    try {
      setExecuting(true);
      setError(null);

      const requestBody: any = {
        metric_id: metric.id,
        dimensions: selectedDimensions,
        time_range_type: timeRangeType,
        sort_by: sortBy || selectedDimensions[0], // Sort by selected dimension or first dimension
        sort_order: sortOrder,
      };

      // Only include limit if it's set
      if (limit !== null && limit > 0) {
        requestBody.limit = limit;
      }

      const response = await apiFetch('/api/metrics/execute', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to execute metric');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setExecuting(false);
    }
  };

  const formatValue = (value: number, valueType: string) => {
    if (valueType === 'currency') {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(value);
    } else if (valueType === 'percentage') {
      return `${value.toFixed(2)}%`;
    } else if (valueType === 'count') {
      return new Intl.NumberFormat('en-US').format(value);
    } else {
      return value % 1 === 0 ? value.toString() : value.toFixed(2);
    }
  };

  const exportToCSV = () => {
    if (!results) return;

    const headers = [...Object.keys(results.results[0]?.dimensions || {}), 'Value'];
    const rows = results.results.map(row => [
      ...Object.values(row.dimensions),
      row.value,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${metric?.metric_code}_${new Date().toISOString()}.csv`;
    a.click();
  };

  // If metric has supported_dimensions, filter to those; otherwise show all dimensions
  const rawAvailable = metric?.supported_dimensions && metric.supported_dimensions.length > 0
    ? dimensions.filter(dim => metric.supported_dimensions?.includes(dim.dimension_code))
    : dimensions;

  // Hide Sales product category for invoice metrics; hide Cost category for order metrics
  const availableDimensions = rawAvailable.filter((dim) => {
    if (metric?.source_table === 'invoices' && dim.dimension_code === 'revenue_category') return false;
    if ((metric?.source_table === 'orders' || metric?.source_table === 'order_items') && dim.dimension_code === 'cost_category') return false;
    return true;
  });

  // Prepare chart data - handle single vs multiple dimensions differently
  const prepareChartData = () => {
    if (!results?.results || results.results.length === 0) return { data: [], series: [] };

    const dimKeys = Object.keys(results.results[0]?.dimensions || {});

    // Helper to check if a dimension is month-like
    const isMonthDimension = (code: string) => code === 'month' || code.endsWith('_month');

    // Single dimension or no dimensions - simple bar chart
    if (dimKeys.length <= 1) {
      const onlyDim = dimKeys[0];
      return {
        data: results.results.map(row => {
          const raw = onlyDim ? row.dimensions[onlyDim] : undefined;
          const name = onlyDim && isMonthDimension(onlyDim)
            ? toYYYYMM(raw)
            : (Object.values(row.dimensions).join(' - ') || 'Total');
          return { name, value: row.value };
        }),
        series: ['value']
      };
    }

    // Multiple dimensions - stacked bar chart
    // Use sort dimension as X-axis, stack by other dimensions
    const sortDimension = sortBy || selectedDimensions[0];
    const stackDimension = dimKeys.find(k => k !== sortDimension) || dimKeys[1];

    // Group data by sort dimension (X-axis)
    const grouped = new Map<string, Map<string, number>>();
    const stackValues = new Set<string>();

    results.results.forEach(row => {
      const xRaw = row.dimensions[sortDimension] ?? 'Unknown';
      const sRaw = row.dimensions[stackDimension] ?? 'Unknown';
      const xValue = isMonthDimension(sortDimension) ? toYYYYMM(xRaw) : xRaw;
      const stackValue = isMonthDimension(stackDimension) ? toYYYYMM(sRaw) : sRaw;

      if (!grouped.has(xValue)) grouped.set(xValue, new Map());
      grouped.get(xValue)!.set(stackValue, row.value);
      stackValues.add(stackValue);
    });

    // Convert to chart format
    const chartData = Array.from(grouped.entries()).map(([xValue, stacks]) => {
      const dataPoint: any = { name: xValue };
      stacks.forEach((value, stackKey) => {
        dataPoint[stackKey] = value;
      });
      return dataPoint;
    });

    return {
      data: chartData,
      series: Array.from(stackValues)
    };
  };

  const { data: chartData, series: chartSeries } = prepareChartData();
  // TanStack Table setup
  const dimensionKeys = useMemo<string[]>(() => {
    if (!results?.results?.length) return []
    return Object.keys(results.results[0].dimensions || {})
  }, [results])

  const flatData = useMemo<any[]>(() => {
    if (!results?.results) return []
    return results.results.map((r) => ({ ...r.dimensions, __value: r.value }))
  }, [results])

  const getHeaderLabel = (code: string) => {
    const dim = dimensions.find((d) => d.dimension_code === code)
    return dim?.display_name || code.replace(/_/g, ' ')
  }

  function toYYYYMM(value: any): string {
    if (value == null) return ''
    if (typeof value === 'string') {
      const m = value.match(/^(\d{4})-(\d{2})/)
      if (m) return `${m[1]}-${m[2]}`
    }
    const d = new Date(value)
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      return `${y}-${m}`
    }
    if (typeof value === 'object' && value && 'year' in value && 'month' in value) {
      const y = (value as any).year
      const m = String((value as any).month).padStart(2, '0')
      return `${y}-${m}`
    }
    return String(value ?? '')
  }

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const dimCols: ColumnDef<any>[] = dimensionKeys.map((key) => {
      const isMonthCol = key === 'month' || key.endsWith('_month')
      return {
        id: key,
        ...(isMonthCol ? { accessorFn: (row) => toYYYYMM(row[key]) } : { accessorKey: key }),
        header: getHeaderLabel(key),
        cell: (info) => String(info.getValue() ?? ''),
        enableColumnFilter: true,
        enableSorting: true,
      }
    })

    const valueCol: ColumnDef<any> = {
      id: '__value',
      accessorKey: '__value',
      header: 'Value',
      cell: (info) => formatValue(info.getValue() as number, metric?.value_type || 'count'),
      enableColumnFilter: false,
      enableSorting: true,
    }

    return [...dimCols, valueCol]
  }, [dimensionKeys, dimensions, metric?.value_type])

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const table = useReactTable({
    data: flatData,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })


  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-center text-gray-500">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!metric) {
    return (
      <Layout>
        <div className="p-6">
          <Alert variant="destructive">
            <AlertDescription>Metric not found</AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/metrics' })}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{metric.display_name}</h1>
            <p className="text-gray-600 mt-1">{metric.description || metric.metric_code}</p>
          </div>
        </div>

        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <CardDescription>Configure how to calculate and display this metric</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dimensions - Full Width */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Dimensions
                {selectedDimensions.length > 0 && (
                  <span className="text-xs text-gray-500 ml-2">
                    ({selectedDimensions.length} selected)
                  </span>
                )}
              </label>
              {availableDimensions.length === 0 ? (
                <div className="text-sm text-gray-500 p-2 border rounded">
                  No dimensions available. Edit the metric to add supported dimensions.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border rounded-lg bg-gray-50">
                  {availableDimensions.map((dim) => (
                    <div key={dim.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`dim-${dim.dimension_code}`}
                        checked={selectedDimensions.includes(dim.dimension_code)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            const newDimensions = [...selectedDimensions, dim.dimension_code];
                            setSelectedDimensions(newDimensions);
                            // Auto-set sort_by to first dimension if not set
                            if (!sortBy && newDimensions.length === 1) {
                              setSortBy(dim.dimension_code);
                            }
                          } else {
                            const newDimensions = selectedDimensions.filter(d => d !== dim.dimension_code);
                            setSelectedDimensions(newDimensions);
                            // Reset sort_by if we removed the dimension we were sorting by
                            if (sortBy === dim.dimension_code) {
                              setSortBy(newDimensions[0] || '');
                            }
                          }
                        }}
                      />
                      <label
                        htmlFor={`dim-${dim.dimension_code}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {dim.display_name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

              {/* Time Range */}
              <div>
                <label className="block text-sm font-medium mb-2">Time Range</label>
                <Select value={timeRangeType} onValueChange={setTimeRangeType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                    <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="all_time">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort By Dimension */}
              {selectedDimensions.length > 1 && (
                <div>
                  <label className="block text-sm font-medium mb-2">Sort By</label>
                  <Select value={sortBy || selectedDimensions[0]} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedDimensions.map((dimCode) => {
                        const dim = dimensions.find(d => d.dimension_code === dimCode);
                        return (
                          <SelectItem key={dimCode} value={dimCode}>
                            {dim?.display_name || dimCode}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium mb-2">Sort Order</label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Chronological (Oldest First)</SelectItem>
                    <SelectItem value="desc">Reverse (Newest First)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Limit */}
              <div>
                <label className="block text-sm font-medium mb-2">Limit</label>
                <Select
                  value={limit?.toString() || 'none'}
                  onValueChange={(v) => setLimit(v === 'none' ? null : parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No limit</SelectItem>
                    <SelectItem value="10">10 rows</SelectItem>
                    <SelectItem value="25">25 rows</SelectItem>
                    <SelectItem value="50">50 rows</SelectItem>
                    <SelectItem value="100">100 rows</SelectItem>
                    <SelectItem value="500">500 rows</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={executeMetric}
                disabled={executing || selectedDimensions.length === 0}
                className="flex items-center gap-2"
              >
                <PlayIcon className="h-4 w-4" />
                {executing ? 'Calculating...' : 'Calculate'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Results */}
        {results && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Results</CardTitle>
                  <CardDescription>
                    {results.total_rows} rows • Calculated in {results.execution_time_ms}ms
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewMode(viewMode === 'table' ? 'chart' : 'table')}
                  >
                    {viewMode === 'table' ? 'Chart' : 'Table'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSQL(!showSQL)}
                  >
                    {showSQL ? 'Hide SQL' : 'Show SQL'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportToCSV}
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {viewMode === 'table' ? (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={header.column.id === '__value' ? 'text-right' : ''}
                          >
                            {header.isPlaceholder ? null : (
                              <button
                                className="flex items-center gap-1 select-none"
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: '▲',
                                  desc: '▼',
                                }[header.column.getIsSorted() as 'asc' | 'desc'] || null}
                              </button>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      {table.getFlatHeaders().map((header) => (
                        <TableHead key={header.id}>
                          {header.column.getCanFilter() ? (
                            <Input
                              placeholder="Filter..."
                              value={(header.column.getFilterValue() as string) ?? ''}
                              onChange={(e) => header.column.setFilterValue(e.target.value)}
                            />
                          ) : null}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cell.column.id === '__value' ? 'text-right font-medium' : ''}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => formatValue(value as number, metric.value_type)}
                    />
                    <Legend />
                    {chartSeries.length === 1 ? (
                      // Single dimension - simple bar
                      <Bar dataKey="value" fill="#3b82f6" name={metric.display_name} />
                    ) : (
                      // Multiple dimensions - stacked bars with different colors
                      chartSeries.map((series, idx) => {
                        const colors = [
                          '#3b82f6', // blue
                          '#10b981', // green
                          '#f59e0b', // amber
                          '#ef4444', // red
                          '#8b5cf6', // violet
                          '#ec4899', // pink
                          '#06b6d4', // cyan
                          '#f97316', // orange
                        ];
                        return (
                          <Bar
                            key={series}
                            dataKey={series}
                            stackId="a"

                            fill={colors[idx % colors.length]}
                            name={series}
                          />
                        );
                      })
                    )}
                  </BarChart>
                </ResponsiveContainer>
              )}
              {showSQL && (results.debug_sql || results.debug_params) && (
                <div className="mt-6">
                  <div className="text-sm text-gray-600 mb-2">Compiled SQL</div>
                  {results.debug_sql ? (
                    <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-80 whitespace-pre-wrap">
{results.debug_sql}
                    </pre>
                  ) : (
                    <div className="text-sm text-gray-500">No SQL available for this metric type.</div>
                  )}
                  {results.debug_params && (
                    <div className="mt-4">
                      <div className="text-sm text-gray-600 mb-2">Parameters</div>
                      <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-80 whitespace-pre-wrap">
{JSON.stringify(results.debug_params, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/metrics/execute/$id')({
  component: MetricExecutePage,
})
